import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/hooks/useAuth';
import { exportEscrow } from '@/lib/content-escrow/export';
import {
  clearOwnerAccountCheck,
  hasPendingOwnerAccountCheck,
  markOwnerAccountCheck,
} from '@/lib/owner-account-check';
import {
  ownerRecoveryOptions,
  type OwnerRecoveryStatus,
} from '@/lib/owner-gate-state';
import styles from './OwnerRecoveryScreen.module.css';

interface OwnerRecoveryScreenProps {
  mode: OwnerRecoveryStatus;
  backupExporter?: (options: {
    contextId: string;
    contextLabel: string;
    passphrase: string;
    downloadBaseName?: string;
  }) => Promise<{ receipt: { ciphertextSha256: string } }>;
}

type BusyAction = 'signin' | 'backup' | 'signout' | null;

/**
 * Protective account surface. It never grants access or changes ownership.
 * A verified backup changes only the confirmation copy and remains fail-closed.
 */
export default function OwnerRecoveryScreen({
  mode,
  backupExporter = exportEscrow,
}: OwnerRecoveryScreenProps) {
  const { t } = useTranslation();
  const { signInWithGoogle, signOut } = useAuth();
  const [busy, setBusy] = useState<BusyAction>(null);
  const [error, setError] = useState(false);
  const [accountCheckFailed, setAccountCheckFailed] = useState(
    hasPendingOwnerAccountCheck,
  );
  const [showBackup, setShowBackup] = useState(false);
  const [showOtherAccountHelp, setShowOtherAccountHelp] = useState(false);
  const [backupPassword, setBackupPassword] = useState('');
  const [backupPasswordAgain, setBackupPasswordAgain] = useState('');
  const [backupSaved, setBackupSaved] = useState(false);
  const options = ownerRecoveryOptions({
    status: mode,
    backupVerified: backupSaved,
    accountCheckFailed,
  });

  async function handleChooseAccount() {
    if (busy || !options.canChooseAccount) return;
    setBusy('signin');
    setError(false);
    setAccountCheckFailed(false);
    markOwnerAccountCheck();
    try {
      await signOut();
      const result = await signInWithGoogle({ selectAccount: true });
      if (result.error) throw new Error(result.error);
    } catch {
      clearOwnerAccountCheck();
      setError(true);
      setBusy(null);
    }
  }

  async function handleSignOut() {
    if (busy) return;
    setBusy('signout');
    setError(false);
    clearOwnerAccountCheck();
    try {
      await signOut();
    } catch {
      setError(true);
      setBusy(null);
    }
  }

  async function handleBackup(event: FormEvent) {
    event.preventDefault();
    if (
      busy ||
      !options.canSaveBackup ||
      backupPassword !== backupPasswordAgain
    ) return;
    setBusy('backup');
    setError(false);
    try {
      const archiveId = crypto.randomUUID();
      const result = await backupExporter({
        contextId: `saved-prayers-${archiveId}`,
        contextLabel: 'saved-prayers',
        passphrase: backupPassword,
        downloadBaseName: 'watch-Him-work-protected-backup',
      });
      if (!result.receipt.ciphertextSha256) throw new Error('backup-not-verified');
      setBackupSaved(true);
      setShowBackup(false);
      setBackupPassword('');
      setBackupPasswordAgain('');
      setBusy(null);
    } catch {
      setError(true);
      setBusy(null);
    }
  }

  const title = options.backupReady
    ? t('ownerSafety.backupReadyTitle')
    : mode === 'maintenance'
      ? t('ownerSafety.maintenanceTitle')
      : mode === 'known-owner-mismatch'
        ? t('ownerSafety.knownTitle')
        : mode === 'unidentified-content-read-only'
          ? t('ownerSafety.unknownReadOnlyTitle')
          : t('ownerSafety.unknownTitle');
  const body = options.backupReady
    ? t('ownerSafety.backupReadyBody')
    : mode === 'maintenance'
      ? t('ownerSafety.maintenanceBody')
      : mode === 'known-owner-mismatch'
        ? t('ownerSafety.knownBody')
        : mode === 'unidentified-content-read-only'
          ? t('ownerSafety.unknownReadOnlyBody')
          : t('ownerSafety.unknownBody');

  return (
    <div className={styles.root}>
      <main className={styles.cluster}>
        <svg
          className={styles.mark}
          width="44"
          height="44"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
          focusable="false"
        >
          <rect x="4" y="10" width="16" height="10" rx="2.5" />
          <path d="M8 10V7.5a4 4 0 0 1 8 0V10" />
        </svg>
        <h1 className={styles.title}>{title}</h1>
        <p className={styles.body}>{body}</p>

        {showBackup && options.canSaveBackup && !options.backupReady ? (
          <form className={styles.backupForm} onSubmit={handleBackup}>
            <label className={styles.inputLabel}>
              <span>{t('ownerSafety.backupPasswordLabel')}</span>
              <input
                className={styles.input}
                type="password"
                autoComplete="new-password"
                value={backupPassword}
                disabled={busy !== null}
                onChange={(event) => setBackupPassword(event.currentTarget.value)}
              />
            </label>
            <p className={styles.help}>{t('ownerSafety.backupPasswordHelp')}</p>
            <label className={styles.inputLabel}>
              <span>{t('ownerSafety.backupPasswordAgainLabel')}</span>
              <input
                className={styles.input}
                type="password"
                autoComplete="new-password"
                value={backupPasswordAgain}
                disabled={busy !== null}
                onChange={(event) => setBackupPasswordAgain(event.currentTarget.value)}
              />
            </label>
            {backupPasswordAgain && backupPassword !== backupPasswordAgain ? (
              <p className={styles.status}>{t('ownerSafety.passwordsDoNotMatch')}</p>
            ) : null}
            <div className={styles.actions}>
              <button
                className={styles.primaryButton}
                type="submit"
                disabled={
                  busy !== null ||
                  Array.from(backupPassword).length < 20 ||
                  backupPassword !== backupPasswordAgain
                }
              >
                {busy === 'backup'
                  ? t('ownerSafety.savingBackup')
                  : t('ownerSafety.saveProtectedBackup')}
              </button>
              <button
                className={styles.quietButton}
                type="button"
                disabled={busy !== null}
                onClick={() => setShowBackup(false)}
              >
                {t('ownerSafety.back')}
              </button>
            </div>
          </form>
        ) : (
          <div className={styles.actions}>
            {options.canChooseAccount ? (
              <button
                className={styles.primaryButton}
                type="button"
                disabled={busy !== null}
                onClick={handleChooseAccount}
              >
                {busy === 'signin'
                  ? t('ownerSafety.openingGoogle')
                  : mode === 'known-owner-mismatch'
                    ? t('ownerSafety.signInOwningAccount')
                    : t('ownerSafety.signInToFind')}
              </button>
            ) : null}
            {options.showSeparateProfileHelp ? (
              <>
                <button
                  className={styles.secondaryButton}
                  type="button"
                  disabled={busy !== null}
                  onClick={() => setShowOtherAccountHelp((visible) => !visible)}
                >
                  {t('ownerSafety.howToUseAnother')}
                </button>
                {showOtherAccountHelp ? (
                  <p className={styles.help}>{t('ownerSafety.otherAccountHelp')}</p>
                ) : null}
              </>
            ) : null}
            {options.canSaveBackup ? (
              <button
                className={styles.secondaryButton}
                type="button"
                disabled={busy !== null}
                onClick={() => {
                  setBackupSaved(false);
                  setShowBackup(true);
                }}
              >
                {options.backupReady
                  ? t('ownerSafety.saveAgain')
                  : t('ownerSafety.saveFirst')}
              </button>
            ) : null}
            {options.canSignOut ? (
              <button
                className={styles.quietButton}
                type="button"
                disabled={busy !== null}
                onClick={handleSignOut}
              >
                {t('ownerSafety.signOut')}
              </button>
            ) : null}
          </div>
        )}

        <p className={styles.status} role="status" aria-live="polite">
          {error
            ? t('ownerSafety.actionFailed')
            : options.showAccountCheckFailed
              ? t('ownerSafety.accountCheckFailed')
              : ''}
        </p>
      </main>
    </div>
  );
}
